import { useState, useEffect, forwardRef, useImperativeHandle, memo } from "react"
import { TextField, Button, Tooltip, Autocomplete, IconButton, Card, CardHeader, CardContent } from "@mui/material"
import DeleteIcon from "@mui/icons-material/Delete"
import { CommonConstant } from "shared/constants/common-constant"
import { rootStore } from "stores/root-store"
import AddIcon from "@mui/icons-material/Add"
import { CustomDataGrid } from "components/common-styles"
import { genId } from "utils/app-helper"

const InventoryCrossReference = forwardRef(({ dimensions, isSubmitting }, ref) => {
  const columns = [
    {
      field: "idx",
      headerName: "No.",
      width: 100,
    },
    {
      field: "quantity_unit_of_measure",
      headerName: "Unit of Measure",
      description: "the unit of measure that this barcode is printed on",
      minWidth: 250,
      flex: 1,
      renderCell: params => (
        <Autocomplete
          disableClearable
          fullWidth
          value={params.value}
          id="quantity_unit_of_measure"
          onChange={(event, x) => onPropChanged(params.row.id, "quantity_unit_of_measure", x.id)}
          options={rootStore.getOptionsByFilter("QUANTITY_UOM").map(option => ({ label: `${option.value}`, id: option.value }))}
          renderInput={paramsInner => <TextField {...paramsInner} />}
        />
      ),
    },
    {
      field: "cross_reference",
      headerName: "Cross Reference",
      description: "the text that is read from scanning the barcode",
      minWidth: 250,
      flex: 1,
      renderCell: params => (
        <TextField
          fullWidth
          value={params.value}
          id="cross_reference"
          onChange={event => onPropChanged(params.row.id, "cross_reference", event.target.value)}
          error={Boolean(params.row.validation.cross_reference)}
          helperText={params.row.validation.cross_reference}
          InputProps={{ maxlength: 25 }}
        />
      ),
    },
    {
      field: "actions",
      headerName: "",
      width: 80,
      renderCell: params =>
        items?.length == 1 ? null : (
          <Tooltip placement="top" title="Delete">
            <IconButton id="btnDeleteCrossRef" aria-label="actions" onClick={() => onDeleteClicked(params.row.id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ),
    },
  ]
  const [items, setItems] = useState([])

  useEffect(() => {
    if (items.length == 0) {
      setItems([...items, createItem(0)])
    }
  }, [items])

  useEffect(() => {
    if (dimensions?.length > 0) {
      const dimension = dimensions[0]
      const result = dimension.cross_reference?.length > 0 ? [...dimension.cross_reference] : []
      result.forEach((p, idx) => {
        p.validation = {}
        p.id = genId()
        p.idx = idx + 1
      })
      setItems(result)
    }
  }, [dimensions])

  useImperativeHandle(ref, () => ({
    getCrossReferencesIfValid() {
      if (validateData(false)) return items

      return null
    },
  }))

  const getErrorMessageOfProp = (newItems, id, prop, val) => {
    if (prop === "cross_reference") {
      if (!val) return "Cross Reference is required."
    }

    return ""
  }

  const onPropChanged = async (id, prop, val) => {
    let newItems = [...items]
    const item = newItems.find(x => x.id === id)
    if (item) {
      if (item.cr_id) item.action = "CHANGE"

      item[prop] = prop == "cross_reference" && val ? val.toUpperCase() : val
      item.validation[prop] = getErrorMessageOfProp(newItems, id, prop, val)

      setItems(newItems)
    }
  }

  const onDeleteClicked = id => {
    const newItems = [...items]
    const item = newItems.find(x => x.id === id)
    if (item) {
      if (item.cr_id) {
        item.action = "DELETE"
        setItems(newItems)
      } else {
        setItems(newItems.filter(x => x.id !== id))
      }
    }
  }

  const validateData = (shouldValidateAllRows = true) => {
    let isValid = true
    const newItems = [...items].filter(item => item.action !== "DELETE")
    if (newItems.length == 1 && !shouldValidateAllRows) return isValid

    newItems.forEach(x => {
      if (!x.validation) {
        x.validation = {}
      }

      x.validation.cross_reference = getErrorMessageOfProp(newItems, x.id, "cross_reference", x.cross_reference)

      if (x.validation.cross_reference) isValid = false
    })

    setItems(newItems)

    return isValid
  }

  const createItem = max => {
    return {
      action: "NEW",
      cr_id: "",
      cross_reference: "",
      quantity_unit_of_measure: "EA",
      id: genId(),
      validation: {},
      idx: max + 1,
    }
  }

  const handleAddNew = () => {
    if (validateData()) {
      const newItems = [...items]
      const max = Math.max(...newItems.map(o => o.idx), 0)
      const item = createItem(max)
      setItems([...newItems, item])
    }
  }

  return (
    <Card id="cardCrossRef">
      <CardHeader
        title="Cross references"
        action={
          <Button id="btnAddCrossRef" color="grey" variant="contained" onClick={handleAddNew} disabled={isSubmitting} startIcon={<AddIcon />}>
            Add New
          </Button>
        }
      />
      <CardContent>
        <CustomDataGrid
          getRowId={row => row.id}
          rows={items.filter(x => x.action !== "DELETE")}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: "line_num", sort: CommonConstant.ASCENDING }],
            },
            pagination: {
              paginationModel: {
                pageSize: 15,
              },
            },
          }}
          pageSizeOptions={[10, 15, 20, 50]}
          rowHeight={85}
          disableRowSelectionOnClick
        />
      </CardContent>
    </Card>
  )
})

export default memo(InventoryCrossReference)
